/**
 * When a `BoardCardTag` shows its tooltip, each one also showing the value
 * while it's cut off and the `detail` whenever there is one:
 * - `whenTruncated`: only then, for values that name their field ("Q3 close").
 * - `fieldName`: names the field while the value fits, for values that don't
 *   ("2 h", "0 / 7", "Manual").
 * - `fieldAndValue`: always the field and the value, for content that isn't
 *   the value's text (an icon-only priority, an avatar).
 * - `none`: never, for content that carries its own tooltips.
 */
export type BoardCardTagTooltip =
    | "whenTruncated"
    | "fieldName"
    | "fieldAndValue"
    | "none";

const SEPARATOR = " · ";

interface BoardCardTagTooltipArgs {
    field: string;
    value: string;
    detail?: string;
    tooltip: BoardCardTagTooltip;
    /** The value's text is cut off with an ellipsis. */
    isTruncated: boolean;
}

/** A `BoardCardTag`'s tooltip text, `Field · Value · Detail` or a part of it; `undefined` shows none. */
export const getBoardCardTagTooltip = ({
    field,
    value,
    detail,
    tooltip,
    isTruncated,
}: BoardCardTagTooltipArgs) => {
    if (tooltip === "none") {
        return undefined;
    }
    const isValueSeen = tooltip !== "fieldAndValue" && !isTruncated;
    if (isValueSeen && tooltip === "whenTruncated" && !detail) {
        return undefined;
    }
    return [field, isValueSeen ? "" : value, detail ?? ""]
        .filter((part) => part !== "")
        .join(SEPARATOR);
};
